import { useCallback, useEffect, useMemo, useRef } from 'react';

const channelInstances: Record<string, BroadcastChannel> = {};
const getChannelName = (name: string): BroadcastChannel => {
  if (!channelInstances[name]) {
    channelInstances[name] = new BroadcastChannel(name);
  }
  return channelInstances[name];
};

/**
 * Custom hook to send message across multiple browser windows using BroadcastChannel API
 * @param channelName
 * @param onMessageReceived
 */
export function useBroadcastChannel<T = string>(
  channelName: `${string}-channel`,
  onMessageReceived: (message: T) => void,
) {
  const channel = useMemo(() => getChannelName(channelName), [channelName]);
  const isSubscribed = useRef(false);

  const onMessageHandler = useCallback(
    (date: T) => {
      onMessageReceived(date);
    },
    [onMessageReceived],
  );

  useEffect(() => {
    if (!isSubscribed.current) {
      channel.onmessage = (e: MessageEvent<T>) => onMessageHandler(e.data);
      channel.onmessageerror = (e: MessageEvent) =>
        console.error(`${channel}-error`, { e });
    }

    return () => {
      if (isSubscribed.current) {
        channel.close();
        isSubscribed.current = true;
      }
    };
  }, [channel, onMessageHandler]);

  const postMessage = useCallback(
    (message: T) => {
      channel?.postMessage(message);
    },
    [channel],
  );

  return { postMessage };
}
